/**
 * URS-F345 — Editor del catalogo de tareas delegables.
 *
 * Solo admin o director_estudio pueden anadir/editar. Soft delete (active=false)
 * conserva el historial. Las tareas por defecto del sistema (is_default=true)
 * no se pueden eliminar.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Pencil, Trash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES_ES = {
  regulatory: 'Regulatorio',
  clinical: 'Clinico',
  data: 'Datos',
  warehouse: 'Almacen',
  other: 'Otros',
};
const CATEGORIES_EN = {
  regulatory: 'Regulatory',
  clinical: 'Clinical',
  data: 'Data',
  warehouse: 'Warehouse',
  other: 'Other',
};

export default function DelegationCatalogPanel({ lang = 'es' }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode, task?}
  const cats = lang === 'es' ? CATEGORIES_ES : CATEGORIES_EN;

  const lb = lang === 'es' ? {
    title: 'Catalogo de tareas delegables',
    subtitle: 'Tareas que el PI puede delegar en cada miembro del equipo del estudio. Las marcadas como "Por defecto" forman parte del sistema y no se pueden eliminar.',
    add: 'Nueva tarea',
    edit: 'Editar tarea',
    label: 'Etiqueta (es)',
    category: 'Categoria',
    order: 'Orden de visualizacion',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete_confirm: '¿Eliminar esta tarea del catalogo? No afectara a los Delegation Log ya firmados.',
    default_badge: 'Por defecto',
    err_label: 'La etiqueta es obligatoria',
    th_id: 'ID',
    th_label: 'Etiqueta',
    th_category: 'Categoria',
    th_order: 'Orden',
    th_actions: 'Acciones',
    empty: 'No hay tareas en el catalogo',
  } : {
    title: 'Delegable tasks catalog',
    subtitle: 'Tasks the PI can delegate to study team members. Tasks marked as "Default" are part of the system and cannot be deleted.',
    add: 'New task',
    edit: 'Edit task',
    label: 'Label (es)',
    category: 'Category',
    order: 'Display order',
    save: 'Save',
    cancel: 'Cancel',
    delete_confirm: 'Delete this task from the catalog? It will not affect already-signed Delegation Logs.',
    default_badge: 'Default',
    err_label: 'Label is required',
    th_id: 'ID',
    th_label: 'Label',
    th_category: 'Category',
    th_order: 'Order',
    th_actions: 'Actions',
    empty: 'No tasks in catalog',
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/delegation-tasks`, { withCredentials: true });
      setTasks(r.data || []);
    } catch (e) {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (task) => {
    if (task.is_default) {
      toast.error(lang === 'es' ? 'Las tareas por defecto no se pueden eliminar' : 'Default tasks cannot be deleted');
      return;
    }
    if (!window.confirm(lb.delete_confirm)) return;
    try {
      await axios.delete(`${API}/api/delegation-tasks/${task.task_id}`, { withCredentials: true });
      await load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-stone-500">{lang === 'es' ? 'Cargando…' : 'Loading…'}</div>;
  }

  return (
    <div className="space-y-4" data-testid="delegation-catalog-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">{lb.title}</h3>
          <p className="text-xs text-stone-500 mt-0.5 max-w-3xl">{lb.subtitle}</p>
        </div>
        <Button data-testid="dlcat-add-btn" onClick={() => setDialog({ mode: 'create' })} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2">
          <Plus weight="bold" size={14} /> {lb.add}
        </Button>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-x-auto">
        {tasks.length === 0 ? (
          <div className="p-8 text-center text-stone-400 text-sm">{lb.empty}</div>
        ) : (
          <table className="w-full text-xs" data-testid="dlcat-table">
            <thead className="bg-stone-100 text-stone-700">
              <tr>
                <th className="px-3 py-2 text-left">{lb.th_id}</th>
                <th className="px-3 py-2 text-left">{lb.th_label}</th>
                <th className="px-3 py-2 text-left">{lb.th_category}</th>
                <th className="px-3 py-2 text-left">{lb.th_order}</th>
                <th className="px-3 py-2 text-right">{lb.th_actions}</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.task_id} className="border-t border-stone-100">
                  <td className="px-3 py-2 font-mono text-stone-500">{t.task_id}</td>
                  <td className="px-3 py-2 text-stone-800">
                    {t.label_es}
                    {t.is_default && <Badge className="ml-2 bg-stone-300 text-stone-700 text-[10px]">{lb.default_badge}</Badge>}
                  </td>
                  <td className="px-3 py-2 text-stone-600">{cats[t.category] || t.category}</td>
                  <td className="px-3 py-2 font-mono text-stone-600">{t.order}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      <Button data-testid={`dlcat-edit-${t.task_id}`} variant="ghost" size="sm" onClick={() => setDialog({ mode: 'edit', task: t })}>
                        <Pencil size={14} />
                      </Button>
                      {!t.is_default && (
                        <Button data-testid={`dlcat-delete-${t.task_id}`} variant="ghost" size="sm" onClick={() => handleDelete(t)}>
                          <Trash size={14} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialog && (
        <TaskDialog
          mode={dialog.mode}
          task={dialog.task}
          lb={lb}
          cats={cats}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskDialog({ mode, task, lb, cats, onClose, onSaved }) {
  const [form, setForm] = useState(() => task ? {
    label_es: task.label_es || '',
    category: task.category || 'other',
    order: task.order ?? 999,
  } : { label_es: '', category: 'other', order: 999 });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.label_es.trim()) { toast.error(lb.err_label); return; }
    setSaving(true);
    try {
      const payload = { label_es: form.label_es.trim(), category: form.category, order: Number(form.order) || 999 };
      if (mode === 'create') {
        await axios.post(`${API}/api/delegation-tasks`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/delegation-tasks/${task.task_id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dlcat-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? lb.add : lb.edit}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">{lb.label} *</Label>
            <Input data-testid="dlcat-label" value={form.label_es} onChange={e => setForm(f => ({ ...f, label_es: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{lb.category}</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="dlcat-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(cats).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{lb.order}</Label>
              <Input data-testid="dlcat-order" type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lb.cancel}</Button>
          <Button data-testid="dlcat-save" onClick={save} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{lb.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
